import {groupByUniqueKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import produce from 'immer';
import React from 'react';

import ExtractByTypename from 'graphql-client/ExtractByTypename';
import StructuredTableOutsideNotebook from 'notebook/structured-tables/StructuredTableOutsideNotebook';
import doesEvaluatedTableHaveAnyErrors from 'notebook/tables/errors/doesEvaluatedTableHaveAnyErrors';
import {EvaluatedTable} from 'notebook/tables/excess/Evaluator';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import getTestProps from 'test/lib/cypress/getTestProps';
import Accordion from 'ui-library/Accordion';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import * as Colors from 'ui-library/Colors';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import {FixFailedRoutingAndMappingModalQuery} from './__generated/FixFailedRoutingAndMappingModalQuery';
import {FromItemIdsToTables, MappingTableData} from './FixFailedRoutingAndMappingConstants';

type ProcessTask = ExtractByTypename<FixFailedRoutingAndMappingModalQuery['processTask'], 'ProcessTaskNode'>;

type ProcessOutput = ProcessTask['outputs'][number];

export const NO_MAPPINGS_INFO_MESSAGE =
  'There is nothing to review for this task/output because only end nodes were selected as the next step.';

export interface PerformManualMappingPaneProps {
  mapperIdsToDownstreamSchemaName: {[id: string]: string};
  fromItems: Array<ProcessTask> | Array<ProcessOutput>;
  fromItemIdsToTables: FromItemIdsToTables | null;
  onChangeFromItemIdsToTables: (newFromItemIdsToTables: FromItemIdsToTables) => void;
}

interface PerformManualMappingSectionProps {
  fromItemName: string;
  fromItemId: string;
  isOpenByDefault?: boolean;
  mappingData: Array<MappingTableData>;
  onChangeMappingData: (newMappingData: Array<MappingTableData>) => void;
}

function PerformManualMappingSection(props: PerformManualMappingSectionProps): JSX.Element {
  const doneIconStyle = props.mappingData.some(({computedTable}) =>
    doesEvaluatedTableHaveAnyErrors(computedTable)
  )
    ? styles.accordionSectionIncomplete
    : styles.accordionSectionCompleted;

  return (
    <Accordion
      isOpenByDefault={props.isOpenByDefault}
      dataTestAttrs={{
        component: 'PerformManualMappingSection',
        element: 'root',
        key: props.fromItemId,
      }}
      header={
        <>
          <span className={css(styles.accordionHeaderName)}>
            <strong>{props.fromItemName}</strong>
          </span>
          <BenchlingIcon icon="done" className={css(doneIconStyle)} />
        </>
      }
    >
      <>
        {props.mappingData.length === 0 ? (
          <InlineBanner bannerType={InlineBannerType.INFO} text={NO_MAPPINGS_INFO_MESSAGE} />
        ) : (
          props.mappingData.map(({structuredTable, mapper}, idx) => (
            <StructuredTableOutsideNotebook
              key={`manual-mapping-${props.fromItemId}-${mapper.id}`}
              cid={`manual-mapping-${props.fromItemId}-${mapper.id}`}
              structuredTable={structuredTable}
              updateHandler={(newTable: ProcessTaskStructuredTable | ProcessOutputStructuredTable) => {
                props.onChangeMappingData(
                  produce(props.mappingData, (draftMappingData) => {
                    draftMappingData[idx].structuredTable = newTable;
                  })
                );
              }}
              onComputedTableChanged={(newComputedTable: EvaluatedTable) => {
                props.onChangeMappingData(
                  produce(props.mappingData, (draftMappingData) => {
                    draftMappingData[idx].computedTable = newComputedTable;
                  })
                );
              }}
              hasPrimarySamples={false}
              hideFormulaBox={true}
              enableFormulas={true}
            />
          ))
        )}
      </>
    </Accordion>
  );
}

export default function PerformManualMappingPane(props: PerformManualMappingPaneProps): JSX.Element {
  if (props.fromItemIdsToTables == null) {
    return <CenteredHorizontalThrobber />;
  }

  const fromItemIdToItem = groupByUniqueKey<ProcessTask | ProcessOutput, string>(
    props.fromItems,
    ({id}) => id
  );

  return (
    <div {...getTestProps({component: 'PerformManualMappingPane', element: 'root'})}>
      {Object.keys(props.fromItemIdsToTables)
        .sort((idA, idB) => fromItemIdToItem[idA].humanId.localeCompare(fromItemIdToItem[idB].humanId))
        .map((fromItemId, idx) => (
          <PerformManualMappingSection
            key={fromItemId}
            isOpenByDefault={idx === 0}
            fromItemId={fromItemId}
            fromItemName={fromItemIdToItem[fromItemId].humanId}
            mappingData={props.fromItemIdsToTables![fromItemId]}
            onChangeMappingData={(newMappingData) => {
              props.onChangeFromItemIdsToTables(
                produce(props.fromItemIdsToTables!, (newFromItemIdsToTables) => {
                  newFromItemIdsToTables[fromItemId] = newMappingData;
                })
              );
            }}
          />
        ))}
    </div>
  );
}

const styles = StyleSheet.create({
  accordionSectionCompleted: {
    color: Colors.BLUE_LIGHT,
  },
  accordionSectionIncomplete: {
    color: Colors.GRAY,
  },
  accordionHeaderName: {
    marginLeft: '8px',
    marginRight: '8px',
  },
});
